import { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Database, Sparkles, Package, Building2, ArrowRight, Globe, Shield, FileText } from 'lucide-react';

const steps = [
  {
    icon: Database,
    secondaryIcon: Globe,
    titleKey: 'processFlow.step1.title',
    descKey: 'processFlow.step1.description',
    color: 'secondary' as const,
  },
  {
    icon: Sparkles,
    secondaryIcon: Shield,
    titleKey: 'processFlow.step2.title',
    descKey: 'processFlow.step2.description',
    color: 'primary' as const,
  },
  {
    icon: Package,
    secondaryIcon: FileText,
    titleKey: 'processFlow.step3.title',
    descKey: 'processFlow.step3.description',
    color: 'secondary' as const,
  },
  {
    icon: Building2,
    secondaryIcon: ArrowRight,
    titleKey: 'processFlow.step4.title',
    descKey: 'processFlow.step4.description',
    color: 'primary' as const,
  },
];

function useInView(threshold = 0.2) {
  const ref = useRef<HTMLDivElement>(null);
  const [isInView, setIsInView] = useState(false);

  useEffect(() => {
    const el = ref.current;
    if (!el) return;

    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setIsInView(true);
          observer.disconnect();
        }
      },
      { threshold }
    );
    observer.observe(el);
    return () => observer.disconnect();
  }, [threshold]);

  return { ref, isInView };
}

export default function ProcessFlow() {
  const { t } = useTranslation();
  const { ref, isInView } = useInView(0.15);

  return (
    <section ref={ref} className="py-10 md:py-16 lg:py-24 bg-background overflow-hidden">
      <div className="container">
        <div className="text-center mb-10 md:mb-16">
          <h2 className="text-2xl md:text-3xl font-bold text-foreground mb-3">
            {t('processFlow.title')}
          </h2>
          <p className="text-muted-foreground max-w-2xl mx-auto text-sm md:text-base">
            {t('processFlow.subtitle')}
          </p>
        </div>

        {/* Desktop: Horizontal flow */}
        <div className="hidden lg:block">
          <div className="relative">
            {/* Connecting line */}
            <div className="absolute top-16 left-[12%] right-[12%] h-0.5 bg-border z-0">
              <div
                className="h-full bg-gradient-to-r from-secondary via-primary to-secondary transition-all duration-[1.5s] ease-out"
                style={{ width: isInView ? '100%' : '0%' }}
              />
            </div>

            <div className="grid grid-cols-4 gap-6 relative z-10">
              {steps.map((step, index) => (
                <ProcessStep
                  key={index}
                  step={step}
                  index={index}
                  isInView={isInView}
                  layout="horizontal"
                />
              ))}
            </div>
          </div>
        </div>

        {/* Mobile/Tablet: Vertical flow */}
        <div className="lg:hidden">
          <div className="relative pl-8 md:pl-12">
            {/* Vertical connecting line */}
            <div className="absolute left-[18px] md:left-[26px] top-4 bottom-4 w-0.5 bg-border">
              <div
                className="w-full bg-gradient-to-b from-secondary via-primary to-secondary transition-all duration-[1.5s] ease-out"
                style={{ height: isInView ? '100%' : '0%' }}
              />
            </div>

            <div className="space-y-8 md:space-y-10">
              {steps.map((step, index) => (
                <ProcessStep
                  key={index}
                  step={step}
                  index={index}
                  isInView={isInView}
                  layout="vertical"
                />
              ))}
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}

interface ProcessStepProps {
  step: (typeof steps)[number];
  index: number;
  isInView: boolean;
  layout: 'horizontal' | 'vertical';
}

function ProcessStep({ step, index, isInView, layout }: ProcessStepProps) {
  const { t } = useTranslation();
  const Icon = step.icon;
  const SecondaryIcon = step.secondaryIcon;

  const colorClasses = step.color === 'primary'
    ? { bg: 'bg-secondary/10', icon: 'text-secondary', ring: 'ring-secondary/20', number: 'bg-primary text-primary-foreground' }
    : { bg: 'bg-secondary/10', icon: 'text-secondary', ring: 'ring-secondary/20', number: 'bg-secondary text-secondary-foreground' };

  const delay = index * 200;

  if (layout === 'vertical') {
    return (
      <div
        className="relative flex items-start gap-4 md:gap-6 transition-all duration-500 ease-out"
        style={{
          opacity: isInView ? 1 : 0,
          transform: isInView ? 'translateX(0)' : 'translateX(-20px)',
          transitionDelay: `${delay}ms`,
        }}
      >
        {/* Step number dot on the line */}
        <div className="absolute -left-8 md:-left-12 flex items-center justify-center">
          <div className={`w-[18px] h-[18px] md:w-[22px] md:h-[22px] rounded-full ${colorClasses.number} flex items-center justify-center text-[10px] md:text-xs font-bold shadow-sm`}>
            {index + 1}
          </div>
        </div>

        {/* Icon */}
        <div className={`relative w-12 h-12 md:w-14 md:h-14 rounded-xl ${colorClasses.bg} ring-1 ${colorClasses.ring} flex items-center justify-center flex-shrink-0`}>
          <Icon className={`w-6 h-6 ${colorClasses.icon}`} />
          <div className={`absolute -bottom-1 -right-1 w-5 h-5 rounded-full bg-background ring-1 ${colorClasses.ring} flex items-center justify-center`}>
            <SecondaryIcon className={`w-3 h-3 ${colorClasses.icon}`} />
          </div>
        </div>

        {/* Text */}
        <div className="pt-1">
          <h3 className="font-semibold text-foreground text-sm md:text-base mb-1">
            {t(step.titleKey)}
          </h3>
          <p className="text-muted-foreground text-xs md:text-sm leading-relaxed">
            {t(step.descKey)}
          </p>
        </div>
      </div>
    );
  }

  // Horizontal layout
  return (
    <div
      className="flex flex-col items-center text-center transition-all duration-500 ease-out"
      style={{
        opacity: isInView ? 1 : 0,
        transform: isInView ? 'translateY(0)' : 'translateY(30px)',
        transitionDelay: `${delay}ms`,
      }}
    >
      {/* Icon with number badge */}
      <div className="relative mb-5">
        <div className={`w-16 h-16 rounded-2xl ${colorClasses.bg} ring-1 ${colorClasses.ring} flex items-center justify-center transition-transform duration-300 hover:scale-110`}>
          <Icon className={`w-7 h-7 ${colorClasses.icon}`} />
        </div>
        {/* Number badge */}
        <div className={`absolute -top-2 -right-2 w-7 h-7 rounded-full ${colorClasses.number} flex items-center justify-center text-xs font-bold shadow-md`}>
          {index + 1}
        </div>
        {/* Secondary icon */}
        <div className={`absolute -bottom-1.5 -left-1.5 w-7 h-7 rounded-full bg-background ring-1 ${colorClasses.ring} flex items-center justify-center`}>
          <SecondaryIcon className={`w-3.5 h-3.5 ${colorClasses.icon}`} />
        </div>
      </div>

      {/* Text */}
      <h3 className="font-semibold text-foreground mb-2">{t(step.titleKey)}</h3>
      <p className="text-muted-foreground text-sm leading-relaxed max-w-[220px]">
        {t(step.descKey)}
      </p>
    </div>
  );
}
